import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Modal, useAuth } from '@byscott-io/corebyscott';
import { families, students } from '../api/client';

export default function FamilyPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [family, setFamily] = useState(null);
  const [showAddStudent, setShowAddStudent] = useState(false);
  const [newStudent, setNewStudent] = useState({
    first_name: '',
    last_name: '',
    birth_date: '',
    grade_level: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const GRADE_LEVELS = ['Pre-K', 'K', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];

  useEffect(() => {
    loadFamily();
  }, [id]);

  const loadFamily = async () => {
    try {
      const response = await families.get(id);
      setFamily(response.data);
    } catch (err) {
      console.error('Failed to load family:', err);
      setError('Failed to load family');
    } finally {
      setLoading(false);
    }
  };

  const handleAddStudent = async (e) => {
    e.preventDefault();
    if (!newStudent.first_name.trim() || !newStudent.last_name.trim()) return;

    setSaving(true);
    try {
      await students.create(id, {
        first_name: newStudent.first_name.trim(),
        last_name: newStudent.last_name.trim(),
        birth_date: newStudent.birth_date || null,
        grade_level: newStudent.grade_level || null,
      });
      setNewStudent({ first_name: '', last_name: '', birth_date: '', grade_level: '' });
      setShowAddStudent(false);
      loadFamily();
    } catch (err) {
      console.error('Failed to add student:', err);
      setError('Failed to add student');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteFamily = async () => {
    if (!confirm('Are you sure you want to delete this family? This will also delete all students.')) {
      return;
    }

    try {
      await families.delete(id);
      navigate('/app/families');
    } catch (err) {
      console.error('Failed to delete family:', err);
      setError('Failed to delete family');
    }
  };

  if (loading) {
    return (
      <div className="px-4 py-6 sm:px-0">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (!family) {
    return (
      <div className="px-4 py-6 sm:px-0">
        <div className="text-red-500">Family not found</div>
      </div>
    );
  }

  return (
    <div className="px-4 py-6 sm:px-0">
      <div className="mb-4">
        <Link to="/app/families" className="text-sm text-indigo-600 hover:text-indigo-500">
          ← Back to Families
        </Link>
      </div>

      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">{family.name}</h1>
          <p className="mt-1 text-sm text-gray-500">
            {family.students?.length || 0} student{family.students?.length !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex space-x-3">
          <button
            type="button"
            onClick={() => setShowAddStudent(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
          >
            Add Student
          </button>
          {isAdmin && (
            <button
              type="button"
              onClick={handleDeleteFamily}
              className="inline-flex items-center px-4 py-2 border border-red-300 shadow-sm text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50"
            >
              Delete Family
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="mt-4 bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Students List */}
      <div className="mt-8">
        <h2 className="text-lg font-medium text-gray-900">Students</h2>
        {family.students?.length === 0 ? (
          <div className="mt-4 text-center py-12 bg-white rounded-lg shadow">
            <span className="text-6xl">🎓</span>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No students yet</h3>
            <p className="mt-1 text-sm text-gray-500">
              Add a student to get started with assessments.
            </p>
            <div className="mt-6">
              <button
                type="button"
                onClick={() => setShowAddStudent(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
              >
                Add Student
              </button>
            </div>
          </div>
        ) : (
          <div className="mt-4 bg-white shadow overflow-hidden rounded-lg">
            <ul className="divide-y divide-gray-200">
              {family.students.map((student) => (
                <li key={student.id}>
                  <Link
                    to={`/app/students/${student.id}`}
                    className="block hover:bg-gray-50"
                  >
                    <div className="px-4 py-4 sm:px-6">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-indigo-600 truncate">
                          {student.full_name}
                        </p>
                        <div className="ml-2 flex-shrink-0 flex space-x-2">
                          {student.grade_level && (
                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                              Grade {student.grade_level}
                            </span>
                          )}
                          {student.age && (
                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                              {student.age} years old
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Add Student Modal */}
      <Modal
        open={showAddStudent}
        onClose={() => setShowAddStudent(false)}
        title="Add Student"
        size="lg"
      >
              <form onSubmit={handleAddStudent}>
                <div>
                  <div className="mt-4 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="first-name" className="block text-sm font-medium text-gray-700">
                          First Name *
                        </label>
                        <input
                          type="text"
                          id="first-name"
                          value={newStudent.first_name}
                          onChange={(e) => setNewStudent({ ...newStudent, first_name: e.target.value })}
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                          autoFocus
                        />
                      </div>
                      <div>
                        <label htmlFor="last-name" className="block text-sm font-medium text-gray-700">
                          Last Name *
                        </label>
                        <input
                          type="text"
                          id="last-name"
                          value={newStudent.last_name}
                          onChange={(e) => setNewStudent({ ...newStudent, last_name: e.target.value })}
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="birth-date" className="block text-sm font-medium text-gray-700">
                        Birth Date
                      </label>
                      <input
                        type="date"
                        id="birth-date"
                        value={newStudent.birth_date}
                        onChange={(e) => setNewStudent({ ...newStudent, birth_date: e.target.value })}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      />
                    </div>
                    <div>
                      <label htmlFor="grade-level" className="block text-sm font-medium text-gray-700">
                        Grade Level
                      </label>
                      <select
                        id="grade-level"
                        value={newStudent.grade_level}
                        onChange={(e) => setNewStudent({ ...newStudent, grade_level: e.target.value })}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      >
                        <option value="">Select grade level</option>
                        {GRADE_LEVELS.map((grade) => (
                          <option key={grade} value={grade}>{grade}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                  <button
                    type="submit"
                    disabled={saving || !newStudent.first_name.trim() || !newStudent.last_name.trim()}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:col-start-2 sm:text-sm disabled:opacity-50"
                  >
                    {saving ? 'Adding...' : 'Add Student'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowAddStudent(false)}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:col-start-1 sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </form>
      </Modal>
    </div>
  );
}
